import React, { useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Share as CapacitorShare } from "@capacitor/share";
import { useLanguage } from "../i18n/LanguageContext";
import { appAlert, appConfirm } from "./AppDialog";
import { useFamilyInvite } from "../hooks/useFamilyInvite";

function formatPreciseDuration(seconds, t) {
  const safe = Math.max(0, Number(seconds || 0));
  const minutes = Math.floor(safe / 60);
  const secs = safe % 60;
  if (safe < 60) return t("familyInvite.secondsLeft", { seconds: safe });
  if (minutes >= 60) return t("familyInvite.minutesLeft", { minutes: 60 });
  return t("familyInvite.minutesSecondsLeft", {
    minutes,
    seconds: String(secs).padStart(2, "0"),
  });
}

async function copyText(text) {
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0;";
    document.body.appendChild(area);
    area.focus();
    area.select();
    area.setSelectionRange(0, text.length);
    const copied = document.execCommand("copy");
    document.body.removeChild(area);
    if (copied) return true;
  } catch {}
  try {
    await navigator.clipboard?.writeText?.(text);
    return true;
  } catch {
    return false;
  }
}

export default function FamilyInvitePanel({
  familyId,
  familyName,
  source = "app",
  variant = "default",
  showHeading = true,
  className = "",
  accentColor = "",
}) {
  const { t } = useLanguage();
  const {
    visibleInvite,
    hasActiveInvite,
    remainingSeconds,
    loading,
    action,
    error,
    createInvite,
    revokeInvite,
    showEducation,
    dismissEducation,
  } = useFamilyInvite({ familyId, enabled: !!familyId });
  const [copied, setCopied] = useState(false);

  const visibleCode = visibleInvite?.code || "";
  const isCodeVisible = !!visibleCode && remainingSeconds > 0;
  const preciseTimeLabel = useMemo(() => formatPreciseDuration(remainingSeconds, t), [remainingSeconds, t]);
  const busy = !!action;

  const create = async () => {
    if (hasActiveInvite) {
      const confirmed = await appConfirm(t("familyInvite.replaceConfirm"), {
        title: t("familyInvite.newCode"),
        confirmText: t("familyInvite.generateNew"),
        cancelText: t("common.cancel"),
      });
      if (!confirmed) return;
    }
    try {
      await createInvite(source);
    } catch (err) {
      await appAlert(err?.message || t("familyInvite.errors.generic"), {
        modal: true,
        title: t("familyInvite.title"),
      });
    }
  };

  const revoke = async () => {
    const confirmed = await appConfirm(t("familyInvite.revokeConfirm"), {
      title: t("familyInvite.revoke"),
      confirmText: t("familyInvite.revoke"),
      cancelText: t("common.cancel"),
      danger: true,
    });
    if (!confirmed) return;
    try {
      await revokeInvite();
    } catch (err) {
      await appAlert(err?.message || t("familyInvite.errors.generic"), {
        modal: true,
        title: t("familyInvite.title"),
      });
    }
  };

  const copyCode = async () => {
    if (!visibleCode) return;
    const ok = await copyText(visibleCode);
    if (!ok) {
      await appAlert(`${t("familyInvite.codeLabel")}: ${visibleCode}`, { modal: true });
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const shareCode = async () => {
    if (!visibleCode) return;
    const text = t("familyInvite.shareText", {
      familyName: familyName || t("home.family"),
      code: visibleCode,
      appUrl: "https://app.aileyonetimi.com",
    });
    const title = t("familyInvite.shareTitle", { familyName: familyName || t("home.family") });
    try {
      if (Capacitor.isNativePlatform()) {
        await CapacitorShare.share({ title, text, dialogTitle: title });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title, text });
        return;
      }
      const ok = await copyText(text);
      await appAlert(ok ? t("familyInvite.shareCopied") : text, { modal: true, title });
    } catch (err) {
      const message = String(err?.message || err || "").toLowerCase();
      if (message.includes("cancel") || message.includes("abort") || message.includes("dismiss")) return;
      const ok = await copyText(text);
      await appAlert(ok ? t("familyInvite.shareCopied") : text, { modal: true, title });
    }
  };

  return (
    <section
      className={`familyInvitePanel familyInvitePanel--${variant} ${className}`.trim()}
      style={accentColor ? { "--primary-color": accentColor } : undefined}
    >
      {showHeading && (
        <div className="familyInviteHeader">
          <div className="familyInviteHeaderIcon" aria-hidden="true">✦</div>
          <div className="familyInviteHeaderText">
            <h3>{t("familyInvite.title")}</h3>
            <p>{t("familyInvite.subtitle")}</p>
          </div>
          <span className="familyInviteSecurityBadge">🔒 {t("familyInvite.secure")}</span>
        </div>
      )}

      {isCodeVisible && (
        <div className="familyInviteCodeCard is-visible">
          <div className="familyInviteCodeMeta">
            <span>{t("familyInvite.codeLabel")}</span>
            <b>{visibleCode}</b>
            <small>{t("familyInvite.singleUseAndTime", { time: preciseTimeLabel })}</small>
          </div>
          <div className="familyInviteStatusDot active" aria-hidden="true" />
        </div>
      )}

      {isCodeVisible ? (
        <div className="familyInviteActions familyInviteActions--revealed">
          <button type="button" className="familyInviteBtn familyInviteBtn--soft" onClick={copyCode} disabled={busy}>
            <span>{copied ? "✓" : "▣"}</span>{copied ? t("familyInvite.copied") : t("familyInvite.copy")}
          </button>
          <button type="button" className="familyInviteBtn familyInviteBtn--primary" onClick={shareCode} disabled={busy}>
            <span>↗</span>{t("familyInvite.share")}
          </button>
          <button type="button" className="familyInviteBtn familyInviteBtn--danger" onClick={revoke} disabled={busy}>
            {action === "revoke" ? t("common.loading") : t("familyInvite.revoke")}
          </button>
        </div>
      ) : (
        <div className="familyInviteActions">
          <button type="button" className="familyInviteBtn familyInviteBtn--primary familyInviteBtn--wide" onClick={create} disabled={loading || busy}>
            <span>＋</span>
            {action === "create"
              ? t("familyInvite.generating")
              : hasActiveInvite
                ? t("familyInvite.generateNew")
                : t("familyInvite.generate")}
          </button>
          {hasActiveInvite && (
            <button type="button" className="familyInviteBtn familyInviteBtn--soft" onClick={revoke} disabled={busy}>
              {action === "revoke" ? t("common.loading") : t("familyInvite.revoke")}
            </button>
          )}
        </div>
      )}

      {hasActiveInvite && !isCodeVisible && (
        <div className="familyInvitePrivacyNote">
          <span aria-hidden="true">◉</span>
          <p>{t("familyInvite.notRecoverable")}</p>
        </div>
      )}

      {showEducation && isCodeVisible && (
        <div className="familyInviteEducation" role="note">
          <button type="button" className="familyInviteEducationClose" onClick={dismissEducation} aria-label={t("common.close")}>×</button>
          <div className="familyInviteEducationIcon">🔒</div>
          <div>
            <strong>{t("familyInvite.educationTitle")}</strong>
            <p>{t("familyInvite.educationText")}</p>
          </div>
        </div>
      )}

      {error && <div className="familyInviteError" role="alert">{error}</div>}
    </section>
  );
}
